/* eslint-disable react-refresh/only-export-components --
 * O único export não-componente é `__resetPwaReloadGuardForTests`, que precisa
 * enxergar o MESMO módulo do guard de reload. Movê-lo para outro arquivo criaria
 * uma segunda instância do guard e o isolamento entre casos deixaria de valer. */
/**
 * PwaUpdateToast — auto-update do PWA com proteção de trabalho não salvo.
 *
 * R6.5 / FRENTE D (2026-05-25) → Rodada de hardening / G2b (2026-07-28).
 *
 * Objetivo (pedido do Matheus): FIM do Ctrl+Shift+R. A aba aberta pega a
 * versão nova sozinha, sem exigir clique — MAS sem atropelar trabalho não
 * salvo.
 *
 * Comportamento quando uma versão nova é detectada (`needRefresh`):
 *   1. SEM trabalho não salvo → atualiza sozinho: mostra o aviso
 *      "Atualizando para a nova versão…" por ~2,5s, chama
 *      `updateServiceWorker(true)` (envia SKIP_WAITING → ativa o SW novo) e
 *      recarrega a página UMA vez.
 *   2. COM trabalho não salvo → NÃO recarrega no susto: mostra o toast
 *      "Nova versão — recarregar agora?" (com botão) E agenda o reload
 *      automático pra PRÓXIMA navegação (mudança de rota), quando a tela não
 *      estiver mais suja. Assim ninguém perde o que estava fazendo.
 *
 * Reload único (sem loop):
 *   Guard booleano de MÓDULO (`jaRecarregou`). Tanto o `controllerchange`
 *   (SW novo assumiu) quanto o fallback por timeout passam por
 *   `recarregarUmaVez()` → no máximo um reload por ciclo de vida da aba.
 *   ZERO storage — é boolean em memória (regra do projeto).
 *
 * Checagem ativa de versão nova:
 *   `registration.update()` a cada 20 min E quando a aba volta a ficar
 *   visível (`visibilitychange`) ou recebe foco. Sem loop — só dispara a
 *   verificação; se houver SW novo, o vite-plugin-pwa seta `needRefresh`.
 *
 * Como uma tela sinaliza "trabalho não salvo":
 *   `useUnsavedGuard(dirty)` de `@/hooks/use-unsaved-guard` (ver doc lá).
 *
 * NÃO REVERTER:
 *   - import virtual:pwa-register/react é injetado em build pelo vite-plugin-pwa.
 *   - updateServiceWorker(true) força skipWaiting — não trocar pra false.
 *   - referrerPolicy/headers de cache do index.html e sw.js são de nginx (fora
 *     do repo) — ver relatório da rodada.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useHasUnsavedWork } from '@/hooks/use-unsaved-guard';

// ────────────────────────────────────────────────────────────────────
// Guard de reload único — boolean de MÓDULO, in-memory (nada de storage).
// Garante no máximo UM reload por ciclo de vida da aba, mesmo que o
// controllerchange e o fallback por timeout disparem juntos.
// ────────────────────────────────────────────────────────────────────
let jaRecarregou = false;

function recarregarUmaVez(): void {
  if (jaRecarregou) return;
  jaRecarregou = true;
  window.location.reload();
}

/** Reseta o guard de reload — USO EXCLUSIVO DE TESTE (isolamento entre casos). */
export function __resetPwaReloadGuardForTests(): void {
  jaRecarregou = false;
}

// A cada 20 min a aba pergunta ao servidor se há SW novo.
const INTERVALO_CHECAGEM_MS = 20 * 60 * 1000;
// Atraso pro usuário LER o aviso antes do reload (sem exigir clique).
const ATRASO_AVISO_MS = 2500;
// Fallback: se o controllerchange não disparar, recarrega mesmo assim.
const ATRASO_FALLBACK_RELOAD_MS = 1200;

/**
 * Log das transições do service worker — degrau 1 do BUGS #59.
 *
 * O smoke de 03/08 pegou a versão ANTIGA na primeira carga depois do deploy, e a
 * leitura do código não bastou para dizer se o auto-reload não dispara ou se ele
 * dispara depois de uma janela de segundos. As duas hipóteses pedem correções
 * diferentes, então **antes de consertar, instrumenta-se**.
 *
 * Fica no `console.info` de propósito: quem abre o console num deploy consegue
 * ler a sequência inteira e dizer qual é o caso, sem precisar de ferramenta.
 */
function logPwa(evento: string, dados?: Record<string, unknown>) {
  console.info(`[PWA] ${evento}`, dados ?? '');
}

export function PwaUpdateToast() {
  const location = useLocation();
  const temTrabalhoSujo = useHasUnsavedWork();
  const [atualizando, setAtualizando] = useState(false);

  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);
  const iniciouRef = useRef(false); // já disparou a atualização?
  const detectouRef = useRef(false); // já reagiu à detecção da versão nova?
  const adiadoRef = useRef(false); // atualização adiada por trabalho sujo?
  const sujoRef = useRef(temTrabalhoSujo); // valor fresco pra ler dentro de effects
  sujoRef.current = temTrabalhoSujo;

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // Guarda a registration pra poder chamar .update() periodicamente.
      if (registration) {
        registrationRef.current = registration;
        logPwa('registrado', { escopo: registration.scope });
        // Estado do SW no momento do registro — é o que diz se já havia uma
        // versão nova esperando quando a página abriu.
        logPwa('estado-inicial', {
          instalando: !!registration.installing,
          esperando: !!registration.waiting,
          ativo: !!registration.active,
        });
      }
    },
    onRegisterError(error) {
      logPwa('erro-no-registro', { erro: String(error) });
    },
  });

  // Dispara a atualização: mostra o aviso, ativa o SW novo e recarrega (uma vez).
  const iniciarAtualizacao = useCallback(() => {
    if (iniciouRef.current) return;
    iniciouRef.current = true;
    logPwa('iniciando-atualizacao');
    setAtualizando(true);
    window.setTimeout(() => {
      // SKIP_WAITING → o SW novo assume; o controllerchange dispara o reload.
      void updateServiceWorker(true);
      // Fallback: garante o reload mesmo se o controllerchange não vier.
      window.setTimeout(recarregarUmaVez, ATRASO_FALLBACK_RELOAD_MS);
    }, ATRASO_AVISO_MS);
  }, [updateServiceWorker]);

  // controllerchange: o SW novo assumiu o controle → recarrega uma vez.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const aoTrocarController = () => {
      logPwa('controllerchange — o SW novo assumiu');
      recarregarUmaVez();
    };
    navigator.serviceWorker.addEventListener('controllerchange', aoTrocarController);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', aoTrocarController);
    };
  }, []);

  // Detecção da versão nova — decide na hora: atualiza sozinho ou adia.
  useEffect(() => {
    if (!needRefresh || detectouRef.current) return;
    detectouRef.current = true;
    logPwa('versao-nova-detectada', { trabalhoEmAndamento: sujoRef.current });
    if (sujoRef.current) {
      // ⚠️ Tem trabalho em andamento → **a pessoa decide a hora**.
      //
      // Mudou em 03/08, quando o Comercial entrou em uso real. Antes isto só
      // adiava para a próxima navegação — o que ainda é decidir por ela, só que
      // mais tarde: o vendedor troca de card no meio de uma proposta e a página
      // recarrega sem que ele tenha pedido.
      adiadoRef.current = true;
      logPwa('adiado — aguardando a pessoa escolher a hora');
    } else {
      // Limpo → atualiza sozinho, mas AVISANDO (ver `atualizando`): antes disto
      // o caminho limpo recarregava em silêncio, e silêncio é o que faz parecer
      // que nada aconteceu.
      iniciarAtualizacao();
    }
  }, [needRefresh, iniciarAtualizacao]);

  // Reload adiado: na PRÓXIMA navegação, se não estiver mais sujo, atualiza.
  const primeiraRotaRef = useRef(true);
  useEffect(() => {
    if (primeiraRotaRef.current) {
      // Ignora a rota inicial (montagem) — só reage a navegações de verdade.
      primeiraRotaRef.current = false;
      return;
    }
    // ⚠️ NÃO atualiza sozinho aqui.
    //
    // Até 03/08 esta linha chamava `iniciarAtualizacao()` assim que a tela
    // deixasse de estar suja. Com gente trabalhando dentro do sistema isso é
    // recarregar a página de alguém sem ela ter pedido — o aviso fica na tela e
    // **quem clica é a pessoa**.
    if (adiadoRef.current && !sujoRef.current) {
      logPwa('trabalho concluído — o aviso segue na tela, esperando o clique');
    }
  }, [location.pathname, iniciarAtualizacao]);

  // Checagem ativa: intervalo de 20 min + volta de foco/visibilidade da aba.
  useEffect(() => {
    const checar = () => {
      void registrationRef.current?.update();
    };
    const intervalo = window.setInterval(checar, INTERVALO_CHECAGEM_MS);
    const aoMudarVisibilidade = () => {
      if (document.visibilityState === 'visible') checar();
    };
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    window.addEventListener('focus', checar);
    return () => {
      window.clearInterval(intervalo);
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
      window.removeEventListener('focus', checar);
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────

  // Atualizando sozinho: aviso informativo, sem exigir clique.
  if (atualizando) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="pwa-update-toast"
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-lg max-w-md"
      >
        <span aria-hidden className="text-xl animate-spin">
          🔄
        </span>
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
            Atualizando para a nova versão…
          </span>
          <span className="text-xs text-ink-2 dark:text-ink-dark-2">Só um instante</span>
        </div>
      </div>
    );
  }

  // Versão nova detectada, MAS há trabalho não salvo → decisão do usuário.
  if (needRefresh && temTrabalhoSujo) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="pwa-update-toast"
        className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-lg max-w-md"
      >
        <span aria-hidden className="text-xl">
          🔄
        </span>
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">Nova versão disponível</span>
          <span className="text-xs text-ink-2 dark:text-ink-dark-2">
            Você tem trabalho não salvo. Recarregar agora?
          </span>
        </div>
        <button
          type="button"
          onClick={() => iniciarAtualizacao()}
          data-testid="pwa-update-toast-update"
          className="px-3 py-1.5 rounded-md bg-red text-white text-xs font-medium hover:bg-red-dark transition-colors"
        >
          Recarregar
        </button>
        <button
          type="button"
          onClick={() => setNeedRefresh(false)}
          aria-label="Agora não"
          data-testid="pwa-update-toast-dismiss"
          className="px-2 py-1 text-ink-2 dark:text-ink-dark-2 hover:text-ink-1 dark:hover:text-ink-dark-1 transition-colors text-lg"
        >
          ×
        </button>
      </div>
    );
  }

  // Sem versão nova (ou já em curso de atualização sem sujeira) → nada na tela.
  return null;
}
